import { useCallback, useEffect, useRef, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import chabadLogo from '../../assets/reshet.png';
import { useNavigate } from 'react-router-dom';
import {
  FormControl,
  Radio,
  InputAdornment,
  FormControlLabel,
  RadioGroup,
  Button,
  Divider,
  Alert
} from '@mui/material';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import AuthLayout from '../../layouts/AuthLayout';
import { reshetGoogleAuthUrl } from '../../constants';
import Copyright from '../../components/auth/Copyright';
import { getUser } from '@/util/getUser';
import LoginMfa from '@/components/auth/LoginMfa';
import { useAppContext } from '@/context/context';
import { Google as GoogleIcon } from '@mui/icons-material';
import instanceAxios from '@/util/axios';
import DynamicDialog from '@/components/dashboard/DaynmicDialog';
import { useLogin } from '@/api/auth';
import { queryClient } from '@/react-query';

const isDevelopment = process.env.NODE_ENV === 'development';

const Login = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [secret, setSecret] = useState('');
  const [isWhatsApp, setIsWhatsApp] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [googleLoginError, setGoogleLoginError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<{ email?: string; firstName?: string; lastName?: string } | null>(null);
  const isMounted = useRef(false);

  const user = getUser();
  const { selectedNav, setUser } = useAppContext();
  const { mutate: login, data, isPending: isLoggingIn, isError: isLoginError } = useLogin();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsWhatsApp((event.target as HTMLInputElement).value === 'whatsapp');
  };

  useEffect(() => {
    if (user) {
      navigate('/feed');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (data?.token) {
      localStorage.setItem('reshet-token', data.token);
      const user = getUser();
      setUser(user);
      queryClient.invalidateQueries();
      if (selectedNav?.link) {
        navigate(selectedNav.link);
      } else if (
        window.localStorage.getItem('redirect') &&
        window.localStorage.getItem('redirect') !== '/login-mfa' &&
        window.localStorage.getItem('redirect') !== '/register'
      ) {
        navigate(window.localStorage.getItem('redirect') as string);
      } else {
        navigate('/kindergarten-leads');
      }
    }
    if (data) {
      setSecret(data.secret);
    }
  }, [data, navigate, selectedNav.link, setUser]);

  const handleLoginWithGoogle = useCallback(
    async (code: string | null) => {
      try {
        setIsAuthenticating(true);

        if (!code) return;
        // Exchange code for tokens
        const response = await instanceAxios.get(`/pvt/auth/login-google?code=${code}`);

        setIsAuthenticating(false);

        if (response.data.token) {
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);

          localStorage.setItem('reshet-token', response.data.token);
          const user = getUser();
          setUser(user);
          queryClient.invalidateQueries();
        } else {
          setGoogleLoginError('לא הצלחנו לקבל גישה מ-Google');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setIsAuthenticating(false);

        if (error.response?.data.isUserNotRegistered && error.response?.data.userInfo) {
          setUserInfo(error.response.data.userInfo);
        }

        // Check for invalid_grant error
        if (
          error.response?.data?.error === 'invalid_grant' ||
          error.response?.data?.message?.includes('Google authentication failed')
        ) {
          setGoogleLoginError('הטוקן פג תוקף. אנא התחבר מחדש ל-Google');
          return;
        }

        if (!error.response?.data?.isUserNotRegistered) {
          setGoogleLoginError('שגיאה בעיבוד הקולבק: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
      }
    },
    [setUser]
  );

  useEffect(() => {
    // * for development purposes only
    if (isMounted.current) return;
    isMounted.current = true;
    // *

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (error) {
      setGoogleLoginError('שגיאה מ-Google: ' + error);
      setIsAuthenticating(false);
      return;
    }

    if (code) {
      handleLoginWithGoogle(code);
    }
  }, [handleLoginWithGoogle]);

  const handleGoogleAuth = async () => {
    try {
      setIsAuthenticating(true);
      setGoogleLoginError(null);

      // Get Google OAuth URL from backend
      const response = await instanceAxios.post(reshetGoogleAuthUrl, {
        redirectUri: `${window.location.origin}/login`
      });
      queryClient.invalidateQueries();
      const data = response.data;

      // Redirect to Google OAuth (instead of popup)
      window.location.href = data.authUrl;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Check for invalid_grant error
      if (
        error.response?.data?.error === 'invalid_grant' ||
        error.response?.data?.message?.includes('Google authentication failed')
      ) {
        setGoogleLoginError('הטוקן פג תוקף. אנא התחבר מחדש ל-Google');
        setIsAuthenticating(false);
        return;
      }

      setGoogleLoginError('שגיאה בהתחברות ל-Google: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setIsAuthenticating(false);
    }
  };

  const onSubmit = async () => {
    await login({ phone, isWhatsApp });
  };

  return (
    <AuthLayout>
      <Container
        sx={{
          width: {
            sm: '90%',
            md: '90%',
            lg: '90%',
            xl: '90%'
          },
          backgroundColor: 'common.white',
          borderRadius: '15px',
          boxShadow: '4',
          alignItems: 'center',
          textAlign: 'center'
        }}
        component="main"
        maxWidth="xs"
      >
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box
            component="img"
            sx={{
              width: '100px',
              marginTop: 4
            }}
            alt="chabad logo"
            src={chabadLogo}
          />
          {!secret ? (
            <>
              {' '}
              <Typography sx={{ marginTop: 1 }} component="h1" variant="subtitle1" color="text.primary">
                ברוכים הבאים לדשבורד הרשת{' '}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <TextField
                  margin="normal"
                  label="מספר נייד"
                  type="text"
                  error={isLoginError}
                  variant="outlined"
                  onChange={event => (event.target.value.length > 8 ? setPhone(event.target.value) : '')}
                  fullWidth
                  inputProps={{
                    'data-testid': 'phone-input'
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <LocalPhoneIcon color="primary" />
                      </InputAdornment>
                    )
                  }}
                />

                <LoadingButton
                  onClick={onSubmit}
                  disabled={phone === ''}
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3 }}
                  loading={isLoggingIn}
                  data-testid="login-button"
                >
                  קבל קוד לנייד{' '}
                </LoadingButton>

                <FormControl sx={{ mt: 2 }}>
                  <RadioGroup
                    row
                    aria-labelledby="message-type-radio-group"
                    defaultValue={isWhatsApp ? 'whatsapp' : 'sms'}
                    name="message-type-radio-buttons"
                    onChange={handleChange}
                    data-testid="message-type-radio-group"
                  >
                    <FormControlLabel value="whatsapp" control={<Radio />} label="וואטסאפ" />
                    <FormControlLabel value="sms" control={<Radio />} label="סמס" />
                  </RadioGroup>
                </FormControl>
                {isLoginError && (
                  <Typography sx={{ marginTop: 1, fontSize: 15, textAlign: 'center' }} color="error.main">
                    {' '}
                    משתמש לא נמצא{' '}
                    <Link
                      color="error.main"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        navigate('/register');
                      }}
                    >
                      לחץ כאן להירשם
                    </Link>{' '}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <LoginMfa secret={secret} phone={phone} isWhatsApp={isWhatsApp} />
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          <Link
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              navigate('/register');
            }}
            fontSize={'13px'}
          >
            עדיין לא רשום?{' '}
          </Link>{' '}
        </Box>

        <Divider sx={{ my: 2 }} />
        <Button
          variant="contained"
          size="medium"
          fullWidth
          startIcon={<GoogleIcon />}
          onClick={handleGoogleAuth}
          disabled={!isDevelopment || isAuthenticating}
          sx={{
            bgcolor: '#4285f4',
            '&:hover': {
              bgcolor: '#3367d6'
            },
            // py: 1.5,
            // px: 4,
            fontSize: '1.1rem'
          }}
        >
          {isAuthenticating ? 'מתחבר...' : 'התחבר עם גוגל'}
        </Button>
        {googleLoginError && (
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            {googleLoginError}
          </Alert>
        )}

        <DynamicDialog
          open={!!userInfo}
          title="החשבון לא רשום במערכת"
          subtitle="נראה שהתחברת עם חשבון גוגל שאינו משויך למערכת שלנו. כדי להמשיך, יש להשלים פרטי הרשמה."
          handleOkClose={() => {
            setUserInfo(null);
            navigate('/register', {
              state: {
                email: userInfo?.email,
                firstName: userInfo?.firstName,
                lastName: userInfo?.lastName
              }
            });
          }}
          handleNotOkClose={() => setUserInfo(null)}
        />

        <Copyright height={40} sx={{ mt: 3, pb: 6 }} />
      </Container>
    </AuthLayout>
  );
};

export default Login;
